import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, CheckSquare, Plus, Trash2, Check, X } from 'lucide-react';
import api from '../utils/api';
import { fmtDate } from '../utils/helpers';
import { buildWaUrl } from '../utils/whatsapp';
import toast from 'react-hot-toast';

const ITEM_STATUSES = ['Pending', 'Received', 'Not Required'];

// Module-scope to prevent focus loss
function WhatsAppModal({ targets, defaultMsg, onClose }) {
  const [msg, setMsg] = useState(defaultMsg || '');
  const textRef = useRef();
  useEffect(() => { textRef.current?.focus(); }, []);
  const withNumber = targets.filter(t => t.whatsapp);
  const sendAll = () => {
    targets.forEach((t, i) => {
      if (!t.whatsapp) return;
      const url = buildWaUrl(t.whatsapp, msg);
      if (url) setTimeout(() => window.open(url, '_blank'), i * 400);
    });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={20} className="text-green-600" />
          <h3 className="font-semibold text-gray-900">WhatsApp — {targets[0]?.name}</h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {targets[0]?.whatsapp && <p className="text-xs text-gray-500 mb-3">+91 {targets[0].whatsapp}</p>}
        <label className="block text-xs font-medium text-gray-700 mb-1">Message (edit before sending)</label>
        <textarea ref={textRef} value={msg} onChange={e => setMsg(e.target.value)} rows={9}
          className="input-field text-sm w-full resize-y font-normal leading-relaxed mb-4" />
        <div className="flex gap-3">
          <button onClick={sendAll} disabled={withNumber.length === 0}
            className="flex-1 py-2 rounded-lg font-medium text-sm bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
            <MessageCircle size={14} /> Send via WhatsApp
          </button>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
const STATUS_COLOR = {
  Pending:        'bg-yellow-100 text-yellow-700',
  Received:       'bg-green-100 text-green-700',
  'Not Required': 'bg-gray-100 text-gray-500',
};

// Module-scope to prevent focus loss
function ItemNotes({ item, checklistId, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.notes || '');
  const save = async () => {
    await api.put(`/checklists/${checklistId}/items/${item.id}`, { ...item, notes: val });
    setEditing(false); onRefresh();
  };
  if (editing) return (
    <div className="flex gap-1 mt-1">
      <input value={val} onChange={e => setVal(e.target.value)} className="input-field text-xs py-1 flex-1" autoFocus
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
      <button onClick={save} className="p-1 bg-green-500 text-white rounded text-xs"><Check size={11} /></button>
      <button onClick={() => setEditing(false)} className="p-1 bg-gray-200 text-gray-600 rounded text-xs"><X size={11} /></button>
    </div>
  );
  return (
    <p onClick={() => setEditing(true)} className="text-xs text-gray-400 mt-0.5 cursor-pointer hover:text-gray-600 italic truncate">
      {item.notes || 'Add notes…'}
    </p>
  );
}

export default function ChecklistDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [reminderLoading, setReminderLoading] = useState(false);
  const [waModal, setWaModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/checklists/${id}`);
      setChecklist(r.data);
    } catch { toast.error('Checklist not found'); navigate('/checklists'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateItemLocal = (itemId, updates) => {
    const today = new Date().toISOString().split('T')[0];
    setChecklist(prev => {
      const newItems = prev.items.map(i => {
        if (i.id !== itemId) return i;
        const merged = { ...i, ...updates };
        if (updates.status === 'Received' && !merged.received_date) merged.received_date = today;
        if (updates.status === 'Pending') merged.received_date = null;
        return merged;
      });
      return { ...prev, items: newItems };
    });
  };

  const updateItem = async (item, updates) => {
    updateItemLocal(item.id, updates);
    try {
      const r = await api.put(`/checklists/${id}/items/${item.id}`, { ...item, ...updates });
      if (r.data.item) updateItemLocal(item.id, r.data.item);
      if (r.data.checklist_status === 'Completed') {
        toast.success('All items received — checklist completed! 🎉');
        load();
      }
    } catch {
      updateItemLocal(item.id, { status: item.status, received_date: item.received_date });
      toast.error('Failed to update — please try again');
    }
  };

  const toggleReceived = (item) => {
    const newStatus = item.status === 'Received' ? 'Pending' : 'Received';
    updateItem(item, { status: newStatus });
  };

  const addItem = async () => {
    if (!newItemName.trim()) return;
    try {
      await api.post(`/checklists/${id}/items`, { item_name: newItemName.trim(), is_mandatory: 0 });
      setNewItemName(''); setAddingItem(false); load();
    } catch { toast.error('Failed'); }
  };

  const deleteItem = async (item) => {
    try {
      await api.delete(`/checklists/${id}/items/${item.id}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const sendReminder = async () => {
    setReminderLoading(true);
    try {
      const r = await api.post(`/checklists/${id}/send-reminder`);
      if (!r.data.whatsapp) {
        toast.error('No WhatsApp number on file. Add it on the client profile.');
        return;
      }
      setWaModal({ targets: [{ name: checklist.client_name, whatsapp: r.data.whatsapp }], defaultMsg: r.data.message });
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'No pending items'); }
    finally { setReminderLoading(false); }
  };

  const markAllReceived = async () => {
    const pending = checklist.items.filter(i => i.status === 'Pending');
    pending.forEach(i => updateItemLocal(i.id, { status: 'Received' }));
    try {
      await Promise.all(pending.map(i => api.put(`/checklists/${id}/items/${i.id}`, { ...i, status: 'Received' })));
      toast.success('All items marked as received ✅');
      load();
    } catch { toast.error('Failed'); load(); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!checklist) return null;

  const items = checklist.items || [];
  const received = items.filter(i => i.status === 'Received').length;
  const pending = items.filter(i => i.status === 'Pending').length;
  const pct = items.length ? Math.round((received / items.length) * 100) : 0;

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/checklists')} className="mt-1 text-gray-400 hover:text-[#1e3a5f]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[#1e3a5f]">{checklist.client_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
            <span className="font-medium text-gray-700">{checklist.work_type}</span>
            {checklist.fy && <span>· FY {checklist.fy}</span>}
            {checklist.period && <span>· {checklist.period}</span>}
            {checklist.assigned_to_name && <span>· {checklist.assigned_to_name}</span>}
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {pending > 0 && (
            <button onClick={sendReminder} disabled={reminderLoading}
              className="flex items-center gap-1.5 bg-green-500 text-white text-xs px-3 py-2 rounded-lg hover:bg-green-600 transition-colors">
              <MessageCircle size={14} /> {reminderLoading ? 'Loading…' : 'WhatsApp Reminder'}
            </button>
          )}
          {pending === 0 && items.length > 0 && checklist.status !== 'Completed' && (
            <button onClick={markAllReceived}
              className="flex items-center gap-1.5 bg-green-500 text-white text-xs px-3 py-2 rounded-lg hover:bg-green-600 transition-colors">
              <CheckSquare size={14} /> Mark All Received
            </button>
          )}
        </div>
      </div>

      {/* Progress card */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Documents Received</span>
          <span className="text-sm font-bold text-[#1e3a5f]">{received} / {items.length}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div className={`h-2.5 rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : 'bg-[#1e3a5f]'}`}
            style={{ width: `${pct}%` }} />
        </div>
        <div className="flex gap-4 mt-2 text-xs text-gray-500">
          <span className="text-green-600 font-medium">{received} Received</span>
          <span className="text-orange-600 font-medium">{pending} Pending</span>
          <span className="text-gray-400">{items.filter(i => i.status === 'Not Required').length} Not Required</span>
        </div>
        {checklist.status === 'Completed' && (
          <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 rounded-lg px-3 py-2 text-sm">
            <CheckSquare size={16} /> Checklist completed{checklist.completed_at ? ` on ${fmtDate(checklist.completed_at?.split('T')[0])}` : ''}
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Documents Checklist</h3>
          <button onClick={() => setAddingItem(a => !a)}
            className="flex items-center gap-1 text-xs text-[#1e3a5f] hover:text-[#f0a500] border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
            <Plus size={13} /> Add Item
          </button>
        </div>

        {addingItem && (
          <div className="flex gap-2 mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
            <input value={newItemName} onChange={e => setNewItemName(e.target.value)} className="input-field text-sm flex-1"
              placeholder="Document name…" autoFocus onKeyDown={e => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') setAddingItem(false); }} />
            <button onClick={addItem} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"><Plus size={12} /> Add</button>
            <button onClick={() => setAddingItem(false)} className="btn-secondary text-xs px-3 py-1.5">Cancel</button>
          </div>
        )}

        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${item.status === 'Received' ? 'bg-green-50 border-green-200' : item.status === 'Not Required' ? 'bg-gray-50 border-gray-100 opacity-60' : 'bg-white border-gray-100 hover:border-gray-200'}`}>
              {/* Checkbox */}
              <button onClick={() => toggleReceived(item)}
                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150 ${item.status === 'Received' ? 'bg-green-500 border-green-500 scale-110' : item.status === 'Not Required' ? 'border-gray-200' : 'border-gray-300 hover:border-green-400 hover:bg-green-50'}`}>
                {item.status === 'Received' && <Check size={12} className="text-white" />}
              </button>

              {/* Name + notes */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${item.status === 'Received' ? 'text-green-700' : item.status === 'Not Required' ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                    {item.item_name}
                  </span>
                  {item.is_mandatory ? (
                    <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded">Required</span>
                  ) : (
                    <span className="text-xs bg-gray-50 text-gray-400 px-1.5 py-0.5 rounded">Optional</span>
                  )}
                </div>
                {item.status === 'Received' && item.received_date && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Check size={11} className="text-green-500" />
                    <span className="text-xs text-green-600 font-medium">Received: {fmtDate(item.received_date)}</span>
                  </div>
                )}
                <ItemNotes item={item} checklistId={id} onRefresh={load} />
              </div>

              {/* Status dropdown */}
              <select value={item.status} onChange={e => updateItem(item, { status: e.target.value })}
                className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${STATUS_COLOR[item.status] || ''}`}>
                {ITEM_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>

              {/* Delete */}
              <button onClick={() => deleteItem(item)} className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors flex-shrink-0">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        {items.length === 0 && (
          <p className="text-center text-gray-400 py-6 text-sm">No items in this checklist. Add items to get started.</p>
        )}
      </div>

      {waModal && <WhatsAppModal targets={waModal.targets} defaultMsg={waModal.defaultMsg} onClose={() => setWaModal(null)} />}
    </div>
  );
}
